<template>
        <div class="datepicker">
            <template v-if="hasInput">
                <div class="input-group">
                    <input class="form-control"
                        type="text"
                        :class="{'with-reset-button': clearButton, 'form-change': changedclass, 'rounded': ! icon, 'form-error': isError || error}" 
                        v-model="inputValue"
                        @click="inputClick"
                        @change="update"
                        @keydown.tab="update"
                        :style="{width:width}"
                        :placeholder="placeholder"
                        :name="name"
                        :display="display"
                        :tabindex="tabindex"
                        :readonly="readonly"
                        :preselect="preselect"
                        :usetodaywhenblank="usetodaywhenblank"
                        :required="required"
                    />
                    <span v-if="icon==true" @click="inputClick" class="input-group-addon glyphicon glyphicon-calendar" aria-hidden="true"></span>
                </div>
                <button v-if="clearButton && value" type="button" class="close" @click="inputValue = ''">
                    <span>&times;</span>
                </button>
            </template>
            <div class="datepicker-popup" :style="paneStyle" @mouseover="handleMouseOver" @mouseout="handleMouseOver" v-show="displayDayView">
            <div class="datepicker-ctrl">
                <span class="datepicker-preBtn glyphicon glyphicon-chevron-left" aria-hidden="true" @click="preNextMonthClick(0)"></span>
                <span class="datepicker-nextBtn glyphicon glyphicon-chevron-right" aria-hidden="true" @click="preNextMonthClick(1)"></span>
            </div>
            <template v-for="(p, pan) in pane">
                <div class="datepicker-inner">
                    <div class="datepicker-body">
                        <p>{{stringifyDayHeader(currDate, pan)}}</p>
                        <div class="datepicker-weekRange">
                            <span v-for="(w, ind) in text.daysOfWeek" :key="ind">{{w}}</span>
                        </div>
                        <div class="datepicker-dateRange">
                            <span v-for="(d, ind) in dateRange[pan]" class="day-cell" :class="getItemClasses(d)" :data-date="stringify(d.date)" @click="daySelect(d.date, $event)" :key="ind"><div>
                            <template v-if="d.sclass !== 'datepicker-item-gray'">
                            {{getSpecailDay(d.date) || d.text}}
                            </template>
                            <template v-else>
                                {{d.text}}
                            </template>
                            <div v-if="d.sclass !== 'datepicker-item-gray'"><slot :name="stringify(d.date)"></slot></div></div>
                        </span>
                        </div>
                    </div>
                </div>
            </template>
            </div>
        </div>
</template>
<script>
import {defineComponent} from 'vue'
import DateFormatUtil from '../mixins/DateFormatUtil.js'
import { DateTime } from 'luxon';
export default defineComponent ({
    mixins: [ DateFormatUtil ],
    emits: ['child-created', 'changedate', 'calendar-rangestart', 'select'],
    props: {
        required: {
            default: false,
            required: false,
            type: Boolean
        },
        isError: {
            requried: false,
            type: Boolean
        },
        modelValue: {
            type: [String, Date],
            required: false,
            default: ''
        },
        preselect: '',
        format: {
            type: String,
            requried: true
        },
        disabledDaysOfWeek: {
            type: Array,
            default() {
                return []
            }
        },
        width: {
            type: String,
            default: '100%'
        },
        clearButton: {
            type: Boolean,
            default: false
        },
        lang: {
            type: String,
            default: navigator.language
        },
        placeholder: {
            type: String
        },
        hasInput: {
            type: Boolean,
            default: true
        },
        pane: {
            type: Number,
            default: 1
        },
        borderWidth: {
            type: Number,
            default: 2
        },
        onDayClick: {
            type: Function,
            default() { }
        },
        changePane: {
            type: Function,
            default() { }
        },
        specialDays: {
            type: Object,
            default() {
                return {}
            }
        },
        rangeBus: {
            type: Function,
            default() {}
        },
        rangeStatus: {
            type: Number,
            default: 0
        },
        readonly: {
            type: Boolean,
            required: false
        },
        display: {
            default: false,
            required: false
        },
        tabindex: {
            required: false,
        },
        name: {
            type: String,
            required: false
        },
        'changedclass': false,
        icon: {
            default: false,
            required: false
        },
        usetodaywhenblank: {
            type: Boolean,
            required: false,
            default: false
        },
    },
    mounted() {
        this._blur = (e) => {
            if (!this.$el.contains(e.target) && this.hasInput) this.close()
        }
        this.$emit('child-created', this)
        this.currDate = this.parse(this.inputValue || '') || this.parse(new Date())
        const year = this.currDate.getFullYear()
        const month = this.currDate.getMonth()
        this.changePane(year, month, this.pane)
        if (!this.hasInput) {
            this.displayDayView = true
            this.updatePaneStyle()
        }
        if (this.rangeStatus) {
            this.eventbus = this.rangeBus()
            if (typeof this.eventbus === 'object' && !this.eventbus.$on) {
                console.warn('Calendar rangeBus doesn\'t exist')
                this.rangeStatus = 0
            }
        }
        if (this.rangeStatus === 2) {
            this._updateRangeStart = (date) => {
                this.rangeStart = date
                this.currDate = date
                this.inputValue = this.stringify(this.currDate)
            }
            this.eventbus.$on('calendar-rangestart', this._updateRangeStart)
        }
        document.addEventListener('click', this._blur)
    },
    beforeUnmount() {
        document.removeEventListener('click', this._blur)
        if (this.rangeStatus === 2) {
            this.eventbus.$off('calendar-rangestart', this._updateRangeStart)
        }
    },
    data() {
        return {
            error: false,
            inputValue: this.modelValue,
            dateFormat: this.format,
            currDate: new Date(),
            dateRange: [],
            decadeRange: [],
            paneStyle: {
                width: ''
            },
            displayDayView: this.display,
            displayMonthView: false,
            displayYearView: false,
            rangeStart: false,
            rangeEnd: false
        }
    },
    watch: {
        inputValue(newValue, oldValue) {
            let lastChar = newValue.slice(-1);

            if (! lastChar.match(/[0-9/-]/) && oldValue == '') {
                this.inputValue = '';
            } else if (! lastChar.match(/[0-9/-]/) && oldValue == '') {
                this.inputValue = oldValue;
            }
            // console.log (newValue, oldValue)
        },
        currDate() {
            this.getDateRange()
        },
        disabledDaysOfWeek() {
            this.getDateRange();
        },
        display(value) {
            this.displayDayView = value;
        },
        preselect: function (newValue, oldValue) {
            if (newValue) {
                // we expect these format
                // 1. ISO (yyyy-mm-dd)
                // 2. mm/dd/yyyy
                let stringVal = this.stringify(this.parseLuxonDefaultMonth(newValue));
                if (stringVal) {
                    this.error = false;
                    this.inputValue = this.stringify(this.parseLuxonDefaultMonth(newValue).toJSDate());
                    this.currDate = DateTime.fromFormat(this.inputValue, this.format).toJSDate();
                } else {
                    if (!this.usetodaywhenblank) this.error = true;
                }
            }
        }
    },
    computed: {
        text() {
            return this.translations(this.lang)
        }
    },
    methods: {
        handleMouseOver(event) {
            let target = event.target
            if (!this.rangeStart) {
                return true
            }
            if (event.type === 'mouseout') {
                return true
            }
            while (this.$el.contains(target) && !~target.className.indexOf('day-cell')) {
                target = target.parentNode
            }
            if (~target.className.indexOf('day-cell') && !~target.className.indexOf('datepicker-item-gray')) {
                const rangeEnd = target.getAttribute('data-date')
                if (this.rangeStart < this.parse(rangeEnd)) {
                    this.rangeEnd = this.parse(rangeEnd)
                }
            }
        },
        getItemClasses(d) {
            const clazz = []
            clazz.push(d.sclass)
            if (this.rangeStart && this.rangeEnd && d.sclass !== 'datepicker-item-gray') {
                if (d.date > this.rangeStart && d.date < this.rangeEnd) {
                    clazz.push('daytoday-range')
                }
                /* eslint-disable eqeqeq */
                if (this.stringify(d.date) == this.stringify(this.rangeStart)) {
                    clazz.push('daytoday-start')
                }
                /* eslint-disable eqeqeq */
                if (this.stringify(d.date) == this.stringify(this.rangeEnd)) {
                    clazz.push('daytoday-end')
                }
            }
            return clazz.join(' ')
        },
        translations(lang) {
            lang = lang || 'en'
            let text = {
                daysOfWeek: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                limit: 'Limit reached ({{limit}} items max).',
                loading: 'Loading...',
                minLength: 'Min. Length',
                months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
                notSelected: 'Nothing Selected',
                required: 'Required',
                search: 'Search'
            }
            return window.VueCalendarLang ? window.VueCalendarLang(lang) : text
        },
        async update() {
            // console.log ('Closing calendar: ' + this.stringify());
            // we expect local format.
            let date = await this.stringify();
            if (date) this.currDate = DateTime.fromFormat(date, this.format).toJSDate();
            this.$emit ('changedate', DateTime.fromFormat(date, this.format).toFormat('MM/dd/yyyy'));
            this.close();
        },
        close() {
            this.displayDayView = this.displayMonthView = this.displayYearView = false
        },
        inputClick() {
            if (this.readonly === true) return;
            this.displayDayView = !this.displayDayView
            this.updatePaneStyle()
        },
        updatePaneStyle() {
            if (!(this.displayMonthView || this.displayYearView)) {
                this.$nextTick(function () {
                    let offsetLeft = this.$el.offsetLeft
                    let offsetWidth = this.$el.querySelector('.datepicker-inner').offsetWidth
                    let popWidth = this.pane * offsetWidth + this.borderWidth // add border
                    this.paneStyle.width = popWidth + 'px'
                    if (this.hasInput) {
                        if (popWidth + offsetLeft > document.documentElement.clientWidth) {
                            this.paneStyle.right = '0px'
                        }
                    } else {
                        this.paneStyle.position = 'initial'
                    }
                    this.$forceUpdate()
                })
            }
        },
        preNextDecadeClick(flag) {
            const year = this.currDate.getFullYear()
            const months = this.currDate.getMonth()
            const date = this.currDate.getDate()
            if (flag === 0) {
                this.currDate = new Date(year - 10, months, date)
            } else {
                this.currDate = new Date(year + 10, months, date)
            }
        },
        preNextMonthClick(flag) {
            const year = this.currDate.getFullYear()
            const month = this.currDate.getMonth()
            const date = this.currDate.getDate()
            if (flag === 0) {
                const preMonth = this.getYearMonth(year, month - 1)
                this.currDate = new Date(preMonth.year, preMonth.month, date)
                this.changePane(preMonth.year, preMonth.month, this.pane)
            } else {
                const nextMonth = this.getYearMonth(year, month + 1)
                this.currDate = new Date(nextMonth.year, nextMonth.month, date)
                this.changePane(nextMonth.year, nextMonth.month, this.pane)
            }
        },
        preNextYearClick(flag) {
            const year = this.currDate.getFullYear()
            const months = this.currDate.getMonth()
            const date = this.currDate.getDate()
            if (flag === 0) {
                this.currDate = new Date(year - 1, months, date)
            } else {
                this.currDate = new Date(year + 1, months, date)
            }
        },
        yearSelect(year) {
            this.displayYearView = false
            this.displayMonthView = true
            this.currDate = new Date(year, this.currDate.getMonth(), this.currDate.getDate())
        },
        daySelect(date, event) {
            let el = event.target
            if (el.classList[0] === 'datepicker-item-disable') {
                return false
            } else {
                if (this.hasInput) {
                    this.currDate = date
                    this.inputValue = this.stringify(this.currDate)
                    this.displayDayView = false
                    if (this.rangeStatus === 1) {
                        this.eventbus.$emit('calendar-rangestart', this.currDate)
                    }
                    this.$emit ('select', this.currDate)
                    this.$emit ('changedate', DateTime.fromFormat(this.stringify(), this.format).toFormat('MM/dd/yyyy'))
                } else {
                    this.onDayClick(date, this.stringify(date))
                }
            }
        },
        switchMonthView() {
            this.displayDayView = false
            this.displayMonthView = true
        },
        switchDecadeView() {
            this.displayMonthView = false
            this.displayYearView = true
        },
        monthSelect(year, index) {
            this.displayMonthView = false
            this.displayDayView = true
            this.currDate = new Date(year, index, this.currDate.getDate())
            this.changePane(year, index, this.pane)
        },
        getYearMonth(year, month) {
            if (month > 11) {
                year++
                month = 0
            } else if (month < 0) {
                year--
                month = 11
            }
            return { year: year, month: month }
        },
        getSpecailDay(v) {
            return this.specialDays[this.stringify(v)]
        },
        stringifyDecadeHeader(date, pan) {
            const yearStr = date.getFullYear().toString()
            const firstYearOfDecade = parseInt(yearStr.substring(0, yearStr.length - 1) + 0, 10) + (pan * 10)
            const lastYearOfDecade = parseInt(firstYearOfDecade, 10) + 10
            return firstYearOfDecade + '-' + lastYearOfDecade
        },
        siblingsMonth(v, n) {
            return new Date(v.getFullYear(), v.getMonth() * 1 + n)
        },
        stringifyDayHeader(date, month = 0) {
            const d = this.siblingsMonth(date, month)
            return this.text.months[d.getMonth()] + ' ' + d.getFullYear()
        },
        parseMonth(date) {
            return this.text.months[date.getMonth()]
        },
        stringifyYearHeader(date, year = 0) {
            return date.getFullYear() + year
        },
        stringify(date) {
            if (!date) date = this.parse()
            if (!date) return ''
            if (typeof date !== 'object') return ''
            return DateTime.fromJSDate(date).toFormat(this.format);
        },
        parse(str = this.inputValue) {
            let date
            if (this.usetodaywhenblank && !str) return null;
            if (typeof str === 'string') date = DateTime.fromFormat(str.replace(/[ \.\,\/-]/g, '/'), this.format);
            else date = str;
            if (!date.isValid && str == '') return new Date()
            return date.isValid ? date.set({hour: 0, minute: 0, second: 0}).toJSDate() : str;
        },
        getDayCount(year, month) {
            const dict = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
            if (month === 1) {
                if ((year % 400 === 0) || (year % 4 === 0 && year % 100 !== 0)) {
                    return 29
                }
            }
            return dict[month]
        },
        getDateRange() {
            this.dateRange = []
            this.decadeRange = []
            for (let p = 0; p < this.pane; p++) {
                let currMonth = this.siblingsMonth(this.currDate, p)
                let time = {
                    year: currMonth.getFullYear(),
                    month: currMonth.getMonth()
                }
                let yearStr = time.year.toString()
                this.decadeRange[p] = []
                let firstYearOfDecade = (yearStr.substring(0, yearStr.length - 1) + 0) - 1
                for (let i = 0; i < 12; i++) {
                    this.decadeRange[p].push({
                        text: firstYearOfDecade + i + p * 10
                    })
                }
                this.dateRange[p] = []
                const currMonthFirstDay = new Date(time.year, time.month, 1)
                let firstDayWeek = currMonthFirstDay.getDay() + 1
                if (firstDayWeek === 0) {
                    firstDayWeek = 7
                }
                const dayCount = this.getDayCount(time.year, time.month)
                if (firstDayWeek > 1) {
                    const preMonth = this.getYearMonth(time.year, time.month - 1)
                    const prevMonthDayCount = this.getDayCount(preMonth.year, preMonth.month)
                    for (let i = 1; i < firstDayWeek; i++) {
                        const dayText = prevMonthDayCount - firstDayWeek + i + 1
                        this.dateRange[p].push({
                            text: dayText,
                            date: new Date(preMonth.year, preMonth.month, dayText),
                            sclass: 'datepicker-item-gray datepicker-item-disable'
                        })
                    }
                }
                for (let i = 1; i <= dayCount; i++) {
                    const date = new Date(time.year, time.month, i)
                    const week = date.getDay()
                    let sclass = ''
                    this.disabledDaysOfWeek.forEach((el) => {
                        if (week === parseInt(el, 10)) sclass = 'datepicker-item-disable'
                    })
                    if (i === this.currDate.getDate()) {
                        if (this.inputValue) {
                            const valueDate = this.parse(this.inputValue)
                            if (valueDate) {
                                if (valueDate.getFullYear() === time.year && valueDate.getMonth() === time.month) {
                                    sclass = 'datepicker-dateRange-item-active'
                                }
                            }
                        }
                    }
                    this.dateRange[p].push({
                        text: i,
                        date: date,
                        sclass: sclass
                    })
                }
            }
        }
    }
});
</script>
<style lang="css">
.glyphicon {
    top: 0px;
  }
  
  .datepicker {
    position: relative;
    padding: 0px;
    display: block;
  }
  
  input.datepicker-input.with-reset-button {
    padding-right: 25px;
  }
  
  .rounded {
    border-top-left-radius: 4px !important;
    border-bottom-left-radius: 4px !important;
    border-top-right-radius: 4px !important;
    border-bottom-right-radius: 4px !important;
  }
  
  .datepicker > button.close {
    position: absolute;
    top: 0;
    right: 0;
    outline: none;
    z-index: 2;
    display: block;
    width: 34px;
    height: 34px;
    line-height: 34px;
    text-align: center;
  }
  
  .datepicker > button.close:focus {
    opacity: 0.2;
  }
  
  .datepicker-popup {
    position: absolute;
    border: 1px solid #ccc;
    border-radius: 5px;
    background: #fff;
    margin-top: 2px;
    z-index: 1000;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
  }
  .datepicker-popup:before, .datepicker-popup:after {
    content: " ";
    display: table;
  }
  .datepicker-popup:after {
    clear: both;
  }
  
  .datepicker-inner {
    width: 218px;
    float: left;
  }
  
  .datepicker-w {
    padding: 10px 10px;
    text-align: left;
  }
  
  .datepicker-body {
    padding: 10px 10px;
    text-align: center;
  }
  
  .datepicker-ctrl p,
  .datepicker-ctrl span,
  .datepicker-body span {
    display: inline-block;
    width: 28px;
    line-height: 28px;
    height: 28px;
  }
  
  .datepicker-ctrl p {
    width: 65%;
  }
  
  .datepicker-ctrl span {
    position: absolute;
  }
  
  .datepicker-title span {
    text-align: center;
  }
  
  .datepicker-body span {
    text-align: left;
  }
  
  .datepicker-monthRange span {
    width: 48px;
    height: 50px;
    line-height: 45px;
  }
  
  .datepicker-item-disable {
    background-color: white !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
  }
  
  .decadeRange span:first-child,
  .decadeRange span:last-child,
  .datepicker-item-disable,
  .datepicker-item-gray {
    color: #999;
    font-weight: normal !important;
  }
  
  .datepicker-dateRange-item-active:hover,
  .datepicker-dateRange-item-active {
    background: rgb(50, 118, 177) !important;
    color: white !important;
  }
  
  .datepicker-monthRange {
    margin-top: 10px;
  }
  
  .datepicker-monthRange span,
  .datepicker-ctrl span,
  .datepicker-ctrl p,
  .datepicker-dateRange span {
    cursor: pointer;
  }
  
  .datepicker-monthRange span:hover,
  .datepicker-ctrl p:hover,
  .datepicker-ctrl i:hover,
  .datepicker-dateRange span:hover,
  .datepicker-dateRange-item-hover {
    background-color: #eeeeee;
  }
  
  .datepicker-dateRange {
    text-align: left;
  }
  
  .daytoday-start,
  .daytoday-start:hover,
  .daytoday-end,
  .daytoday-end:hover {
    background: rgb(50, 118, 177) !important;
    color: white !important;
  }
  
  .datepicker-dateRange .daytoday-range,
  .datepicker-dateRange .daytoday-range:hover {
    background-color: #ddd;
  }
  
  .datepicker-weekRange {
    text-align: left;
  }
  
  .datepicker-weekRange span {
    font-weight: bold;
  }
  
  .datepicker-label {
    background-color: #f8f8f8;
    font-weight: 700;
    padding: 7px 0;
    text-align: center;
  }
  
  .datepicker-ctrl {
    position: relative;
    /*height: 30px;*/
    line-height: 30px;
    font-weight: bold;
    text-align: center;
  }
  
  .month-btn {
    font-weight: bold;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
  
  .datepicker-preBtn {
    left: 2px;
  }
  
  .datepicker-nextBtn {
    right: 2px;
  }
  
  .day-cell {
    font-weight: bold;
  }
</style>